import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  Gender,
  GradeCondition,
  JobField,
  JobFieldsJobIdFkeyInverseInput,
  JobFieldsJobIdFkeyJobFieldsCreateInput,
  JobStatus,
  Language,
  useCreateJobMutation,
  usePipelinesQuery,
} from "../../graphql/generated/graphql";
import { FormValues } from "./types";
import { safeParseInt } from "../../utils";
import { convertFaInputNumbers } from "../../utils/convertFaInputNumber";
import { SkillsAutoComplete } from "../SkillsAutoComplete/SkillsAutoComplete";
import LoadingButton from "../Button/LoadingButton";
import AddressComponent from "../CreateJobFields/Address";
import GenderComponent from "../CreateJobFields/Gender";
import GradeConditions from "../CreateJobFields/GradeConditions";
import FieldComponent from "../CreateJobFields/Fields";

interface CreateJobFormProps {
  onSuccess: () => void;
  setOpen: (open: boolean) => void;
}

export default function CreateJobForm({
  onSuccess,
  setOpen,
}: CreateJobFormProps) {
  const [createJobMutation, { loading, error }] = useCreateJobMutation();
  const { data: pipelinesData } = usePipelinesQuery();
  const navigate = useNavigate();
  const [isModalOpen] = useState(true);
  const [selectedSkills] = useState<{ value: string; label: string }[]>([]);

  const pipelineId = useMemo(
    () => pipelinesData?.pipelines?.nodes[0]?.id || null,
    [pipelinesData]
  );

  const initialValues: FormValues = useMemo(
    () => ({
      status: JobStatus.Published,
      departmentId: null,
      isRemote: false,
      address: "",
      country: 1,
      state: null,
      city: "",
      education: null,
      position: 0,
      pipelineId: pipelineId!,
      languages: [Language.Fa, Language.En],
      translations: [
        {
          create: [
            {
              lang: Language.Fa,
              title: "",
              description: "",
              requirements: "",
              fieldOfStudy: "",
              orientation: "",
            },
          ],
        },
      ],
      tags: [],
      owners: [],
      fields: {
        create: [
          { type: JobField.Gender, position: 1, required: true },
          { type: JobField.MilitaryStatus, position: 2, required: true },
          { type: JobField.Resume, position: 3, required: true },
          { type: JobField.Birthday, position: 4, required: true },
        ] as JobFieldsJobIdFkeyJobFieldsCreateInput[],
      } as JobFieldsJobIdFkeyInverseInput,
      jobQuestionnaires: [],
      workExperienceCondition: 0,
      ageCondition: [25, 75],
      maxAgeCondition: 75,
      minAgeCondition: 25,
      gradeConditions: [],
      knowledges: [],
      skills: [],
      competencies: [],
      genders: null,
    }),
    [pipelineId]
  );

  const validationSchema = Yup.object({
    translations: Yup.array().of(
      Yup.object({
        create: Yup.array().of(
          Yup.object({
            title: Yup.string().required("وارد کردن عنوان الزامی است."),
            description: Yup.string().required("وارد کردن توضیحات الزامی است."),
            requirements: Yup.string(),
          })
        ),
      })
    ),
  });

  const handleGradeConditionsChange = (
    setFieldValue: (field: string, value: any) => void,
    newGradeConditions: GradeCondition[] | null
  ) => {
    setFieldValue("gradeConditions", newGradeConditions);
  };

  const handleSkillSelection = (
    skills: { value: string; label: string }[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const skillIds = skills.map((skill) => skill.value);
    setFieldValue("skills", skillIds);
  };

  return (
    isModalOpen && (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          try {
            const response = await createJobMutation({
              variables: {
                input: {
                  job: {
                    city: values.city || null,
                    languages: values.languages,
                    position: values.position,
                    status: JobStatus.Published,
                    translations: {
                      create: [
                        {
                          lang: Language.Fa,
                          title:
                            values.translations?.[0]?.create?.[0]?.title || "",
                          description:
                            values.translations?.[0]?.create?.[0]
                              ?.description || "",
                          requirements:
                            values.translations?.[0]?.create?.[0]
                              ?.requirements || "",
                          fieldOfStudy:
                            values.translations?.[0].create?.[0]
                              ?.fieldOfStudy || "",
                          orientation:
                            values.translations?.[0].create?.[0]?.orientation ||
                            "",
                        },
                      ],
                    },
                    pipelineId: pipelineId,
                    departmentId: values.departmentId || null,
                    competencies: values.competencies || [],
                    isRemote: values.isRemote,
                    address: values.address,
                    country: values.country,
                    state: values.state ? safeParseInt(values.state, 7) : null,
                    education: values.education,
                    tags: values.tags,
                    owners: null,
                    fields: values.fields || { create: [] },
                    jobQuestionnaires: null,
                    workExperienceCondition: values.workExperienceCondition,
                    maxAgeCondition: values.maxAgeCondition || null,
                    minAgeCondition: values.minAgeCondition || null,
                    gradeConditions: values.gradeConditions || null,
                    knowledges: values.knowledges,
                    skills: values.skills,
                    genders: values.genders,
                  },
                },
              },
            });
            if (response && response.data) {
              toast.success("باموفقیت ثبت شد");
              navigate("/jobs");
            } else {
              toast.error("خطا در ثبت شغل! لطفا دوباره تلاش کنید");
            }

            onSuccess();
          } catch (e) {
            toast.error("خطا در ثبت شغل! لطفا دوباره تلاش کنید");
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {(props) => (
          <form className="m-1 text-right" onSubmit={props.handleSubmit}>
            <h1 className="text-xl mx-3 mb-2 text-right">موقعیت شغلی جدید</h1>
            <div className="border shadow-sm m-2 p-2 bg-slate-50 rounded-lg flex flex-col">
              <label htmlFor="title" className="m-1">
                عنوان
              </label>
              <Field
                type="text"
                name="translations[0].create[0].title"
                className="shadow-sm border border-gray-300 p-1 rounded-lg outline-none focus:border-gray1"
                placeholder="عنوان را وارد کنید"
              />
              {Array.isArray(props.errors.translations) &&
              props.errors.translations[0].create[0].title ? (
                <div className="text-red-500 text-xs">
                  {props.errors.translations[0].create[0].title}
                </div>
              ) : null}
              <label htmlFor="description" className="m-1">
                توضیحات
              </label>
              <Field
                as="textarea"
                type="text"
                name="translations[0].create[0].description"
                className="shadow-sm border border-gray-300 rounded-lg p-1 outline-none focus:border-gray1"
                placeholder="توضیحات را وارد کنید"
              />
              {Array.isArray(props.errors.translations) &&
              props.errors.translations[0].create[0].description ? (
                <div className="text-red-500 text-xs">
                  {props.errors.translations[0].create[0].description}
                </div>
              ) : null}
            </div>
            <div className="border shadow m-2 p-2 bg-slate-50 rounded-lg">
              <h2 className="my-3 font-medium text-lg">تحصیلات: </h2>
              <div className="my-3">
                <label htmlFor="fieldOfStudy">رشته</label>
                <Field
                  className="w-[100%] border border-gray-300 shadow-sm mx-auto mt-1 rounded-sm p-0.5"
                  type="text"
                  id="fieldOfStudy"
                  name="translations[0].create[0].fieldOfStudy"
                />
              </div>
              <GradeConditions
                selectedGradeConditions={props.values.gradeConditions ?? []}
                setSelectedGradeConditions={(
                  newGradeConditions: GradeCondition[] | null
                ) =>
                  handleGradeConditionsChange(
                    props.setFieldValue,
                    newGradeConditions
                  )
                }
              />
              <div className="my-3">
                <label htmlFor="orientation">گرایش</label>
                <Field
                  className="w-[100%] border border-gray-300 shadow-sm mx-auto mt-1 rounded-sm p-0.5"
                  type="text"
                  id="orientation"
                  name="translations[0].create[0].orientation"
                />
              </div>
            </div>
            <div className="hidden">
              <FieldComponent
                fieldKeys={
                  props.values.fields?.create?.map((f) => f.type) || []
                }
                setFieldValue={props.setFieldValue}
              />
            </div>
            <div className="border shadow px-2 m-2 py-3 bg-slate-50 rounded-lg">
              <label htmlFor="age">بازه سنی</label>
              <div className="flex flex-row">
                <div className="m-3">
                  <label htmlFor="minAgeCondition" className="ml-3">
                    حداقل سن:
                  </label>
                  <input
                    type="text"
                    id="minAgeCondition"
                    name="minAgeCondition"
                    value={props.values.minAgeCondition ?? ""}
                    onChange={(e) => {
                      const convertedValue = convertFaInputNumbers(
                        e.target.value
                      );
                      props.setFieldValue("minAgeCondition", convertedValue);
                    }}
                    onBlur={props.handleBlur}
                    className="w-[100%] border border-gray-300 shadow-sm mx-auto mt-1 rounded-sm p-0.5"
                  />
                </div>
                <div className="m-3">
                  <label htmlFor="maxAgeCondition" className="ml-3">
                    حداکثر سن:
                  </label>
                  <input
                    type="text"
                    id="maxAgeCondition"
                    name="maxAgeCondition"
                    value={props.values.maxAgeCondition ?? ""}
                    onChange={(e) => {
                      const convertedValue = convertFaInputNumbers(
                        e.target.value
                      );
                      props.setFieldValue("maxAgeCondition", convertedValue);
                    }}
                    onBlur={props.handleBlur}
                    className="w-[100%] border shadow-sm mx-auto mt-1 rounded-sm p-0.5"
                  />
                </div>
              </div>
            </div>
            <GenderComponent
              gender={props.values.genders as Gender | null}
              onChange={(newGender) =>
                props.setFieldValue("genders", newGender)
              }
            />
            <div className="border shadow px-2 m-2 py-3 bg-slate-50 rounded-lg flex flex-col">
              <label htmlFor="isRemote" className="m-1">
                امکان دورکاری
              </label>
              <Field
                as="select"
                name="isRemote"
                className="w-[100%] border shadow-sm m-auto mt-1 rounded-sm p-0.5 focus:border-gray1"
                onChange={(e: { target: { value: string } }) =>
                  props.setFieldValue("isRemote", e.target.value === "true")
                }
              >
                <option value="true">بله</option>
                <option value="false">خیر</option>
              </Field>
            </div>
            <div className="border shadow m-2 px-2 py-3 bg-slate-50 rounded-lg">
              <label htmlFor="workExperienceCondition">سابقه کاری (سال)</label>
              <input
                type="text"
                id="workExperienceCondition"
                name="workExperienceCondition"
                value={props.values.workExperienceCondition ?? ""}
                onChange={(e) => {
                  const convertedValue = convertFaInputNumbers(e.target.value);
                  props.setFieldValue(
                    "workExperienceCondition",
                    convertedValue
                  );
                }}
                onBlur={props.handleBlur}
                className="w-[100%] border border-gray-300 shadow-sm mx-auto mt-1 rounded-sm p-0.5"
              />
              {props.errors.workExperienceCondition &&
              props.touched.workExperienceCondition ? (
                <div className="text-red-500 text-xs">
                  {props.errors.workExperienceCondition}
                </div>
              ) : null}
            </div>
            <div className="border shadow m-2 p-2 bg-slate-50 rounded-lg">
              <h2 className="font-medium my-2 text-lg">دانش و مهارت </h2>
              <SkillsAutoComplete
                onSkillsChange={(skills) =>
                  handleSkillSelection(skills, props.setFieldValue)
                }
              />
              {selectedSkills.length > 0 && (
                <div className="selected-skills-list ">
                  <ul>
                    {selectedSkills.map((skill) => (
                      <li key={skill.value}></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <AddressComponent
              countryValue={props.values.country || null}
              stateValue={props.values.state || null}
              cityValue={props.values.city || ""}
              addressValue={props.values.address || ""}
              onCountryChange={(newCountry) =>
                props.setFieldValue("country", newCountry)
              }
              onStateChange={(newState) =>
                props.setFieldValue("state", newState)
              }
              onCityChange={(newCity) => props.setFieldValue("city", newCity)}
              onAddressChange={(newAddress) =>
                props.setFieldValue("address", newAddress)
              }
            />
            <div className="flex justify-between items-center w-[95%] m-auto gap-x-4 mt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-primary text-white font-semibold p-2 px-4 rounded-lg w-1/2 م-auto"
              >
                {loading ? <LoadingButton /> : "ثبت شغل"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="bg-red-500 text-white font-semibold p-2 px-4 rounded-lg w-1/2 م-auto"
              >
                لغو
              </button>
            </div>
            {error && toast.error("خطا در ثبت شغل! لطفا دوباره تلاش کنید")}
          </form>
        )}
      </Formik>
    )
  );
}
